import React, { useCallback, useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { COLORS, FORCE_COLOR_OPTIONS, generateId } from './types';

const FORCE_SCALE = 4;
const VIEWBOX_PADDING = 24;

const normalizeAngle = (degrees) => ((Number(degrees) % 360) + 360) % 360;

const normalizeForce = (force, index = 0) => ({
  id: force?.id || generateId(),
  label: force?.label || (index === 0 ? 'F' : `F${index + 1}`),
  magnitude: Number.isFinite(Number(force?.magnitude)) ? Number(force.magnitude) : 10,
  directionDeg: Number.isFinite(Number(force?.directionDeg)) ? Number(force.directionDeg) : 0,
  color: force?.color || COLORS[index % COLORS.length],
});

const getForceVector = (force, width, height) => {
  const minDim = Math.max(1, Math.min(width, height));
  const bodyRadius = Math.max(18, minDim * 0.16);
  const center = { x: width / 2, y: height / 2 };
  const magnitude = Math.abs(Number(force.magnitude) || 0);
  const length = magnitude * FORCE_SCALE;
  const directionRad = Number(force.directionDeg || 0) * Math.PI / 180;
  const ux = Math.cos(directionRad);
  const uy = -Math.sin(directionRad);
  const angle = normalizeAngle(force.directionDeg || 0);
  const arcRadius = bodyRadius + 10;
  const arcRad = angle * Math.PI / 180;
  const arcStart = { x: center.x + arcRadius, y: center.y };
  const arcEnd = {
    x: center.x + arcRadius * Math.cos(arcRad),
    y: center.y - arcRadius * Math.sin(arcRad),
  };
  const labelRad = (angle / 2) * Math.PI / 180;
  const arcLabel = {
    x: center.x + (arcRadius + 12) * Math.cos(labelRad),
    y: center.y - (arcRadius + 12) * Math.sin(labelRad),
  };
  return {
    start: {
      x: center.x + ux * bodyRadius,
      y: center.y + uy * bodyRadius,
    },
    end: {
      x: center.x + ux * (bodyRadius + length),
      y: center.y + uy * (bodyRadius + length),
    },
    center,
    bodyRadius,
    angle,
    arcRadius,
    arcStart,
    arcEnd,
    arcLabel,
  };
};

const FbdComponent = ({ element, onUpdate }) => {
  const width = Math.max(1, element.width || 420);
  const height = Math.max(1, element.height || 320);
  const drawingHeight = Math.max(150, Math.round(height * 0.48));
  const forces = useMemo(
    () => (Array.isArray(element.forces) && element.forces.length
      ? element.forces.map(normalizeForce)
      : [normalizeForce(null)]),
    [element.forces]
  );

  const updateForce = useCallback((id, updates) => {
    onUpdate({
      forces: forces.map((force) => (
        force.id === id ? { ...force, ...updates } : force
      )),
    });
  }, [forces, onUpdate]);

  const addForce = useCallback(() => {
    onUpdate({
      forces: [
        ...forces,
        normalizeForce({
          id: generateId(),
          label: `F${forces.length + 1}`,
          magnitude: 10,
          directionDeg: 90,
          color: COLORS[forces.length % COLORS.length],
        }, forces.length),
      ],
    });
  }, [forces, onUpdate]);

  const removeForce = useCallback((id) => {
    if (forces.length <= 1) return;
    onUpdate({ forces: forces.filter((force) => force.id !== id) });
  }, [forces, onUpdate]);

  const markerIds = useMemo(() => (
    forces.map((force) => `fbd-arrow-${element.id}-${force.id}`.replace(/[^a-zA-Z0-9_-]/g, '-'))
  ), [element.id, forces]);

  const diagram = useMemo(() => {
    const vectors = forces.map((force) => getForceVector(force, width, drawingHeight));
    const center = { x: width / 2, y: drawingHeight / 2 };
    const bodyRadius = Math.max(18, Math.min(width, drawingHeight) * 0.16);
    const points = [
      { x: center.x - bodyRadius, y: center.y - bodyRadius },
      { x: center.x + bodyRadius, y: center.y + bodyRadius },
      { x: 0, y: center.y },
      { x: width, y: center.y },
      { x: center.x, y: 0 },
      { x: center.x, y: drawingHeight },
      ...vectors.flatMap((vector) => [
        vector.start,
        vector.end,
        vector.arcStart,
        vector.arcEnd,
        vector.arcLabel,
      ]),
    ];
    const minX = Math.min(...points.map((point) => point.x)) - VIEWBOX_PADDING;
    const maxX = Math.max(...points.map((point) => point.x)) + VIEWBOX_PADDING;
    const minY = Math.min(...points.map((point) => point.y)) - VIEWBOX_PADDING;
    const maxY = Math.max(...points.map((point) => point.y)) + VIEWBOX_PADDING;
    return {
      vectors,
      center,
      bodyRadius,
      viewBox: `${minX} ${minY} ${maxX - minX} ${maxY - minY}`,
      axis: {
        left: minX + 12,
        right: maxX - 12,
        top: minY + 12,
        bottom: maxY - 12,
      },
    };
  }, [drawingHeight, forces, width]);

  return (
    <div className="wb-fbd">
      <div className="wb-fbd-stage" style={{ height: drawingHeight }}>
        <svg
          className="wb-fbd-svg"
          viewBox={diagram.viewBox}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Free body diagram"
        >
          <defs>
            <marker
              id={`fbd-axis-${element.id}`.replace(/[^a-zA-Z0-9_-]/g, '-')}
              markerWidth="7"
              markerHeight="7"
              refX="6"
              refY="3.5"
              orient="auto"
            >
              <path d="M 0 0 L 7 3.5 L 0 7 z" fill="#94a3b8" />
            </marker>
            {forces.map((force, index) => (
              <marker
                key={markerIds[index]}
                id={markerIds[index]}
                markerWidth="9"
                markerHeight="9"
                refX="8"
                refY="4.5"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path d="M 0 0 L 9 4.5 L 0 9 z" fill={force.color} />
              </marker>
            ))}
          </defs>
          <line
            x1={diagram.axis.left}
            y1={diagram.center.y}
            x2={diagram.axis.right}
            y2={diagram.center.y}
            className="wb-fbd-axis"
            markerEnd={`url(#${`fbd-axis-${element.id}`.replace(/[^a-zA-Z0-9_-]/g, '-')})`}
          />
          <line
            x1={diagram.center.x}
            y1={diagram.axis.bottom}
            x2={diagram.center.x}
            y2={diagram.axis.top}
            className="wb-fbd-axis"
            markerEnd={`url(#${`fbd-axis-${element.id}`.replace(/[^a-zA-Z0-9_-]/g, '-')})`}
          />
          <text x={diagram.axis.right - 10} y={diagram.center.y - 8} className="wb-fbd-axis-label">x</text>
          <text x={diagram.center.x + 8} y={diagram.axis.top + 16} className="wb-fbd-axis-label">y</text>
          <circle
            cx={diagram.center.x}
            cy={diagram.center.y}
            r={diagram.bodyRadius}
            className="wb-fbd-body"
          />
          <text x={diagram.center.x} y={diagram.center.y + 4} textAnchor="middle" className="wb-fbd-body-label">
            {element.bodyLabel || 'Object'}
          </text>
          {forces.map((force, index) => {
            const vector = diagram.vectors[index];
            const showArc = vector.angle > 0.5 && vector.angle < 359.5;
            return (
              <g key={force.id}>
                {showArc && (
                  <>
                    <path
                      d={`M ${vector.arcStart.x} ${vector.arcStart.y} A ${vector.arcRadius} ${vector.arcRadius} 0 ${vector.angle > 180 ? 1 : 0} 0 ${vector.arcEnd.x} ${vector.arcEnd.y}`}
                      className="wb-fbd-angle-arc"
                      stroke={force.color}
                    />
                    <text
                      x={vector.arcLabel.x}
                      y={vector.arcLabel.y}
                      className="wb-fbd-angle-label"
                      fill={force.color}
                    >
                      {Math.round(vector.angle)}°
                    </text>
                  </>
                )}
                <line
                  x1={vector.start.x}
                  y1={vector.start.y}
                  x2={vector.end.x}
                  y2={vector.end.y}
                  stroke={force.color}
                  strokeWidth="3"
                  markerEnd={`url(#${markerIds[index]})`}
                />
                <text
                  x={vector.end.x + 8}
                  y={vector.end.y - 8}
                  className="wb-fbd-force-label"
                  fill={force.color}
                >
                  {force.label || 'F'} = {force.magnitude}N
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="wb-fbd-editor">
        <label className="wb-fbd-body-input">
          <span>Body</span>
          <input
            value={element.bodyLabel || ''}
            onChange={(event) => onUpdate({ bodyLabel: event.target.value })}
          />
        </label>

        <div className="wb-fbd-force-list">
          {forces.map((force) => (
            <div className="wb-fbd-force-row" key={force.id}>
              <input
                className="wb-fbd-label-input"
                value={force.label}
                aria-label="Force label"
                onChange={(event) => updateForce(force.id, { label: event.target.value })}
              />
              <input
                type="number"
                value={force.magnitude}
                aria-label="Force magnitude"
                onChange={(event) => updateForce(force.id, { magnitude: Number(event.target.value) })}
              />
              <input
                type="number"
                value={force.directionDeg}
                aria-label="Force direction"
                onChange={(event) => updateForce(force.id, { directionDeg: Number(event.target.value) })}
              />
              <select
                value={force.color}
                aria-label="Force color"
                onChange={(event) => updateForce(force.id, { color: event.target.value })}
              >
                {FORCE_COLOR_OPTIONS.map((color) => (
                  <option key={color.hex} value={color.hex}>{color.name}</option>
                ))}
              </select>
              <button
                type="button"
                className="wb-fbd-remove"
                onClick={() => removeForce(force.id)}
                disabled={forces.length <= 1}
                title="Remove force"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>

        <button type="button" className="wb-fbd-add" onClick={addForce}>
          <Plus size={13} />
          Add force
        </button>
      </div>
    </div>
  );
};

export default React.memo(FbdComponent);
